/**
 * Case-insensitive predicates for special file-extension detection.
 *
 * R14 walkthrough — established the convention: extensions are matched
 * case-INSENSITIVELY for TYPE DETECTION. The file PATH stays
 * case-sensitive (FS-level), but the question "what kind of file is
 * this?" answers the same whether the user wrote `.MiniApp` or `.miniapp`.
 *
 * This matches every industry consumer of file extensions:
 *   - macOS / Windows (case-insensitive filesystems)
 *   - Linux applications via `xdg-open`, `file`, MIME detection (even on
 *     case-sensitive ext4 the application layer is case-insensitive)
 *   - Web browsers (image.JPG, image.png interchangeable)
 *   - Cloud storage (report.PDF opens as PDF in Dropbox/Drive/OneDrive)
 *   - HTTP Content-Type (RFC 9110 — media types case-insensitive)
 *
 * Pure predicates, no runtime deps — kept in `@papan/types` so any
 * consumer (api, web, files, collab, share-links) imports the same
 * canonical check.
 *
 * #166 finished the rollout (owner, 2026-10-06: industry practice): every
 * suffix test in the codebase goes through this module — TS through the
 * predicates, SQL through `miniappNameSqlPredicate` / `pointerNameSqlPredicate`
 * — including the pointer ROW classifiers (`isPointerRow`) and the I125 rename
 * chokepoint. `no-raw-extension-suffix.test.ts` refuses a hand-written one.
 */

const lower = (name: string): string => name.toLowerCase()

/**
 * All special-extension predicates below share the SAME literal-form
 * exclusion rule (R15 #113 walkthrough): a name that is LITERALLY the
 * suffix (any case) is rejected. The user can still create such a name
 * — content-driven model, we don't reject at write — but the predicate
 * returns false because there's no meaningful "thing it refers to":
 *   - `.miniapp` folder has no app name for audit logs / consent dialogs
 *   - `.workpapan` folder has no workspace name
 *   - `.shared` pointer file has no source-name to resolve against
 *   - `.chain` pointer file has no chain-target name
 *   - `.pmeta` sidecar has no paired source file
 *
 * Caller-visible effect: downstream classifiers (share-link router,
 * edit-perm banner, preview-root-resolver, drive UI labels) treat
 * literal-form names as plain folders/files rather than special ones.
 */

/**
 * Miniapp (Mode B) folder suffixes, canonical-default FIRST.
 *
 * `.pdapp` is canonical as of freeze doc 3c-ii — the name-test gate is passed,
 * so CREATION emits it (via {@link miniappFolderName}) everywhere at once:
 * scaffold, folder elevation, workspace create. `.pda` is its short alias.
 * `.miniapp` is the historical form and stays in DETECTION **indefinitely**;
 * only creation moved. Same shape as `.project` vs `.workpapan`.
 *
 * Renames and copies must PRESERVE the folder's actual suffix (use
 * {@link miniappFolderExt}) — never rewrite an existing `.miniapp` folder to
 * `.pdapp`, and never the reverse.
 *
 * Order is load-bearing in two places: {@link miniappFolderName} takes the head
 * as canonical, and {@link miniappNameSqlPredicate} emits the arms in this
 * order. Adding an alias here widens every raw-SQL site automatically; putting
 * a non-canonical entry first silently changes what new apps are named.
 */
export const MINIAPP_FOLDER_EXT = '.pdapp'
export const MINIAPP_FOLDER_EXTS = ['.pdapp', '.pda', '.miniapp'] as const

/** Is the folder a Mode B miniapp elevation target? (`.miniapp`/`.pdapp`/`.pda`, any case) */
export function isMiniappFolder(name: string): boolean {
  const l = lower(name.replace(/\/$/, ''))
  return MINIAPP_FOLDER_EXTS.some((ext) => l.endsWith(ext) && l !== ext)
}

/**
 * Which share-link kind an item is eligible for: `app` (it RUNS — a `.pdapp`
 * folder or an HTML entry) vs `quick_view` (everything else, which opens as
 * content).
 *
 * Lives here, beside `isMiniappFolder`, because THREE callers need it and none
 * can reach the others: the share-links API route, the SPA's Share dialog, and
 * the agent's `share_link` tool (worker-side, and it may not import a browser
 * client module). It was duplicated in the first two before the third needed
 * it — and a rule about which URL an item gets is exactly the kind that must
 * not drift, since the two answers produce different, both-plausible links.
 */
export function shareKindForFile(args: { name: string; isFolder: boolean }): 'app' | 'quick_view' {
  if (args.isFolder) return isMiniappFolder(args.name) ? 'app' : 'quick_view'
  const l = lower(args.name)
  return l.endsWith('.html') || l.endsWith('.htm') ? 'app' : 'quick_view'
}

/** True if the item can be shared as a running app (the gated Share mode). */
export function isAppEligibleForFile(args: { name: string; isFolder: boolean }): boolean {
  return shareKindForFile(args) === 'app'
}

/**
 * The miniapp suffix a folder name actually carries (`.miniapp`/`.pdapp`/`.pda`,
 * matched case-insensitively but returned in canonical lower form), or `null`
 * if it isn't a miniapp folder. Use this to preserve the author's chosen alias
 * on copy-dedup and rename instead of assuming `.miniapp`'s length.
 */
export function miniappFolderExt(name: string): typeof MINIAPP_FOLDER_EXTS[number] | null {
  const l = lower(name.replace(/\/$/, ''))
  return MINIAPP_FOLDER_EXTS.find((ext) => l.endsWith(ext) && l !== ext) ?? null
}

/** Build the on-disk folder name for a NEW miniapp from its display name (canonical `.miniapp`). */
export function miniappFolderName(displayName: string): string {
  return `${displayName}${MINIAPP_FOLDER_EXT}`
}

/**
 * The app-folder suffixes as REGISTRY KEYS — no leading dot — DERIVED from
 * {@link MINIAPP_FOLDER_EXTS}.
 *
 * Two client registries are keyed on a bare lowercase extension rather than on a
 * filename, so neither can call {@link isMiniappFolder}: the editor registry
 * (`features/collab/client/editor-registry.ts`, whose key is the workspace tab's
 * `type` — the raw last extension of the tab title) and the file-icon registry
 * (`shared/core-ui/ui/FileIcon.tsx`).
 *
 * Both hand-wrote `'miniapp'` and both silently stopped working the moment
 * `MINIAPP_FOLDER_EXT` moved to `.pdapp` (issue #365): a `.pdapp` folder opened
 * in the TEXT editor — so the PDApp editor never mounted, and the ▷/⚙ buttons
 * that signal it did nothing at all, with no error anywhere — while its icon fell
 * back to a plain folder. Deriving here is what stops the next alias added above
 * from reopening either one.
 */
export const MINIAPP_EDITOR_KEYS = MINIAPP_FOLDER_EXTS.map((ext) => ext.slice(1)) as readonly string[]

/**
 * Is this bare extension key (a workspace tab's `type`, an icon registry key) one
 * of the app-folder suffixes? The filename-shaped sibling is
 * {@link isMiniappFolder}; use that whenever you have the name.
 */
export function isMiniappTabType(type: string): boolean {
  return MINIAPP_EDITOR_KEYS.includes(lower(type))
}

/** Strip the miniapp extension (`.miniapp`/`.pdapp`/`.pda`) to the display name. */
export function stripMiniappFolderExt(name: string): string {
  return name.replace(/\/$/, '').replace(/\.(?:miniapp|pdapp|pda)$/i, '')
}

/**
 * `isMiniappFolder` as a raw-SQL boolean, DERIVED from {@link MINIAPP_FOLDER_EXTS}.
 *
 * Several hot queries can't call a TS predicate — they filter inside a recursive
 * CTE (storage-by-category, launcher curation, hook discovery). Each one that
 * hand-wrote `LIKE '%.miniapp'` silently stopped seeing apps the moment the alias
 * set grew: a `.pdapp` app was miscategorized in storage stats and invisible to
 * launcher curation, with no error anywhere. Emitting the arms from the SAME
 * constant is what makes that drift impossible — the SQL widens when the array
 * does.
 *
 * Mirrors the predicate exactly, INCLUDING its literal-form exclusion (a folder
 * named exactly `.pdapp`, with no basename, is not an app — see the shared rule
 * above). Case-insensitive via `lower()`, matching the TS side.
 *
 * `col` and `pointerSuffix` are interpolated VERBATIM and must never carry user
 * input — pass a trusted column identifier and one of our own suffix constants.
 * Callers wrap the result in `sql.raw()`.
 *
 * @param col           trusted column identifier, e.g. `name` or `f.name`
 * @param pointerSuffix appended to each extension for pointer rows, e.g. `.shared`
 *                      → matches `X.pdapp.shared`
 */
export function miniappNameSqlPredicate(col: string = 'name', pointerSuffix: string = ''): string {
  const arms = MINIAPP_FOLDER_EXTS.map((ext) => {
    const full = `${ext}${pointerSuffix}`
    return `(lower(${col}) LIKE '%${full}' AND lower(${col}) <> '${full}')`
  })
  return `(${arms.join(' OR ')})`
}

/**
 * Project folder suffixes, canonical-default first. `.project` is the canonical
 * extension going forward (project revamp, GH #166 follow-up); `.workpapan` is
 * the legacy form and stays recognized so existing folders keep working until
 * the Stage 4 backfill renames them; `.workspace` is a reserved alias for
 * forward use. Detection accepts ANY of them; creation emits `.project` only.
 */
export const PROJECT_FOLDER_EXT = '.project'
export const PROJECT_FOLDER_EXTS = ['.project', '.workpapan', '.workspace'] as const

/** Project folder (`.project` canonical, or `.workpapan`/`.workspace` aliases; any case). */
export function isProjectFolder(name: string): boolean {
  const l = lower(name)
  return PROJECT_FOLDER_EXTS.some((ext) => l.endsWith(ext) && l !== ext)
}

/**
 * The project extension a folder name carries (`.project` / `.workpapan` /
 * `.workspace`), or `null` if it isn't a project folder. Used for UI labels
 * that should reflect the actual on-disk extension rather than the canonical one.
 */
export function projectFolderExt(name: string): typeof PROJECT_FOLDER_EXTS[number] | null {
  const l = lower(name.replace(/\/$/, ''))
  return PROJECT_FOLDER_EXTS.find((ext) => l.endsWith(ext) && l !== ext) ?? null
}

/**
 * @deprecated Use {@link isProjectFolder}. Kept as an alias so the pre-revamp
 * call sites compile unchanged during the terminology rename (Stage 2). Now
 * also matches `.project`, not just `.workpapan`.
 */
export const isWorkpapanFolder = isProjectFolder

/**
 * System-usage workspace suffix. `.workspace` folders are workspace-CAPABLE
 * (they get a workspace DB row, agent sessions, and sandbox mounts — hence
 * `.workspace` is one of `PROJECT_FOLDER_EXTS` and `isProjectFolder` is TRUE
 * for them), but they are system plumbing, NOT user-facing projects: they host
 * the ambient AI-chat surfaces provisioned under `System/.Archangel/`.
 *
 * `isSystemWorkspaceFolder` is the narrower flag user-facing surfaces filter on
 * — home Projects cards, Projects listings, recent-projects, find-for-file.
 * The distinction is exhaustive within the workspace family:
 *   - `.workspace` → isProjectFolder TRUE  + isSystemWorkspaceFolder TRUE  (hide from users)
 *   - `.project`   → isProjectFolder TRUE  + isSystemWorkspaceFolder FALSE (show to users)
 * So a user-facing filter is `isProjectFolder(n) && !isSystemWorkspaceFolder(n)`.
 *
 * See docs/plans/2026-07-12_WORKSPACE_ASSISTANT_SCOPED_SANDBOX.md §3.3.
 */
export const SYSTEM_WORKSPACE_EXT = '.workspace'

/** System-usage workspace folder (`.workspace` suffix, any case). */
export function isSystemWorkspaceFolder(name: string): boolean {
  const l = lower(name)
  return l.endsWith(SYSTEM_WORKSPACE_EXT) && l !== SYSTEM_WORKSPACE_EXT
}

/** Build the on-disk folder name for a system workspace from its display name. */
export function systemWorkspaceFolderName(displayName: string): string {
  return `${displayName}${SYSTEM_WORKSPACE_EXT}`
}

/** Strip a project folder extension (`.project` / `.workpapan` / `.workspace`) to the display name. */
export function stripProjectFolderExt(name: string): string {
  return name.replace(/\/$/, '').replace(/\.(?:project|workpapan|workspace)$/i, '')
}

/** Build the on-disk folder name for a project from its display name. */
export function projectFolderName(displayName: string): string {
  return `${displayName}${PROJECT_FOLDER_EXT}`
}

/**
 * Human display name for an app or app-workspace folder. Strips a project
 * extension (`.project`/`.workpapan`/`.workspace`) THEN a `.miniapp`/`.html`
 * marker, so a workspace shell like `Smoke App.miniapp.project` and a bare
 * `Smoke App.miniapp` both display as `Smoke App`. This is the FILENAME
 * fallback — prefer an app's manifest `name` when it's available.
 */
export function appDisplayName(nameOrPath: string): string {
  const base = (nameOrPath.split('/').filter(Boolean).pop() || nameOrPath).replace(/\/$/, '')
  let n = stripProjectFolderExt(base)
  n = stripMiniappFolderExt(n)
  n = n.replace(/\.html?$/i, '')
  return n
}

/**
 * Firebird database file extensions — `.fdb` and `.firebird` (the editor's, firebird-registration.ts) and `.gdb`,
 * Firebird's older one. ONE list (integration review round 1, 2026-10-17): the editor and its routes treated `.firebird`
 * as a live db while the E7 sync passes knew only `.fdb` / `.gdb`, so a reconcile could commit a torn `.firebird` file
 * and the mirror repair rename a head over a live one. A Firebird server changes the file IN PLACE.
 */
export const FIREBIRD_DB_EXTS = ['.fdb', '.gdb', '.firebird'] as const

/** A Firebird database name (any of {@link FIREBIRD_DB_EXTS}, any case) — the path's LAST segment; a name that is
 *  literally the suffix is not one. */
export function isFirebirdDbName(nameOrPath: string): boolean {
  const l = lower(nameOrPath.slice(nameOrPath.lastIndexOf('/') + 1))
  return FIREBIRD_DB_EXTS.some((ext) => l.endsWith(ext) && l !== ext)
}

/** A delimited-text table name (`.csv` / `.tsv`, any case) — the path's LAST segment; a name that is literally the
 *  suffix is not one. */
export function isCsvOrTsvName(nameOrPath: string): boolean {
  const l = lower(nameOrPath.slice(nameOrPath.lastIndexOf('/') + 1))
  return (l.endsWith('.csv') && l !== '.csv') || (l.endsWith('.tsv') && l !== '.tsv')
}

/** The pointer suffixes. Matched case-INsensitively everywhere (#166) — TS through the
 *  predicates below, SQL through {@link pointerNameSqlPredicate}. Never test them by hand. */
export const SHARED_POINTER_EXT = '.shared'
export const CHAIN_POINTER_EXT = '.chain'

/** Cross-tenant share pointer (`.shared` suffix, any case). */
export function isSharedPointer(name: string): boolean {
  const l = lower(name)
  return l.endsWith(SHARED_POINTER_EXT) && l !== SHARED_POINTER_EXT
}

/** Same-tenant chain pointer (`.chain` suffix, any case). */
export function isChainPointer(name: string): boolean {
  const l = lower(name)
  return l.endsWith(CHAIN_POINTER_EXT) && l !== CHAIN_POINTER_EXT
}

/**
 * SQL twin of {@link isSharedPointer} / {@link isChainPointer}: does `col` end in the
 * pointer suffix (any case, literal-form excluded)? Same contract as
 * {@link miniappNameSqlPredicate}: `col` is a TRUSTED column identifier, never user input;
 * callers wrap the result in `sql.raw()`. Every raw-SQL suffix test goes through this, so
 * the query and the runtime cannot disagree on case (#166).
 */
export function pointerNameSqlPredicate(kind: 'shared' | 'chain', col: string = 'name'): string {
  const ext = kind === 'shared' ? SHARED_POINTER_EXT : CHAIN_POINTER_EXT
  return `(lower(${col}) LIKE '%${ext}' AND lower(${col}) <> '${ext}')`
}

/**
 * Strip a single trailing pointer suffix (`.shared` / `.chain`, any case) to
 * the INNER name: `report.pdf.shared` → `report.pdf`. A pointer row's local
 * content is a metadata mirror — the bytes a viewer receives stream from the
 * source — so any TYPE decision keyed on the name (viewer pick, parser
 * branch, delimiter) must use the inner name, never the raw row name.
 * Non-pointer names come back unchanged; exactly ONE suffix is stripped
 * (`a.shared.shared` → `a.shared`).
 */
export function stripPointerSuffix(name: string): string {
  return name.replace(/\.(?:shared|chain)$/i, '')
}

/**
 * (#327) Does this row BEHAVE as a package — a real `.miniapp`/`.project`
 * folder, OR a `.shared` mount whose SOURCE is one (`MyApp.miniapp.shared`)?
 *
 * A folder-target `.shared` mirrors the source name and appends `.shared`, so
 * every name-suffix package detector misses it at the mount root — the
 * recipient got the contents but not the behavior. These pair the inner name
 * with the row KIND, which is the load-bearing half: a hand-made folder a user
 * literally names `X.miniapp.shared` carries `fileType: 'folder'` and stays a
 * plain folder, so the name alone can never fake a package (the same
 * kind-anchored rule as the #157 write gate and the Phase 1c icon lift).
 *
 * `.chain` (same-tenant pointer) is deliberately NOT included: it references a
 * target by id and would deref to the real folder rather than mount it, so it
 * needs its own routing rather than this predicate. Track A ships `.shared`.
 */
export function presentsAsMiniapp(name: string, fileType?: string | null, isFolder?: boolean): boolean {
  // A LIVE `.shared` pointer is a FILE row that PRESENTS as its target —
  // `is_folder` is false on the row itself, and only the LISTING lifts it (the
  // detail endpoint returns the raw false). So this arm must not test it: a
  // `.miniapp` inner name already implies the target is a folder. Testing
  // `isFolder` here is what made a launched mount fall through to the loose-HTML
  // branch and report "this item is not an HTML page".
  //
  // LIVE, though — the suffix as well as the kind. Renaming a pointer off its
  // suffix demotes it to a plain file and leaves `file_type` behind, so a
  // kind-only test took this arm for a demoted row and skipped the very
  // `isFolder` check that should reject it. Renaming `X.miniapp.shared` to
  // `X.miniapp` then routed a plain FILE to the app launcher, producing the same
  // "not an HTML page" symptom from the opposite direction.
  //
  // Spelled out rather than reusing `isLiveMountRow`, which also accepts
  // `.chain` — and chain is deliberately excluded here (see the doc above): it
  // dereferences to the real folder rather than mounting, so it needs its own
  // routing. Reusing the general helper would have quietly enabled routing that
  // was deferred on purpose.
  if (fileType === 'shared' && isSharedPointer(name)) return isMiniappFolder(stripPointerSuffix(name))
  // Native package: a real folder. `undefined` means the caller doesn't know
  // (older call sites) — only an explicit `false` rules it out. A demoted
  // pointer lands here and is correctly rejected, being a file.
  return isFolder !== false && isMiniappFolder(name)
}

/** Project counterpart of `presentsAsMiniapp` — see its doc for both gates. */
export function presentsAsProject(name: string, fileType?: string | null, isFolder?: boolean): boolean {
  if (fileType === 'shared' && isSharedPointer(name)) return isProjectFolder(stripPointerSuffix(name))
  return isFolder !== false && isProjectFolder(name)
}

/**
 * (#327) Is this row a LIVE mount — both flagged as a pointer AND still wearing
 * the suffix?
 *
 * The codebase rule, previously stated only in a comment on `MountRow`: **the
 * extension is authoritative, not the `file_type` column.** Renaming a pointer
 * away from its suffix DEMOTES it to a plain file (#157 v2b; since I125 the files
 * chokepoint clears its kind and target too) — the name is what decides.
 *
 * That invariant lived privately inside `mount-links.ts`'s `isMountChild`, which
 * is why three later call sites all missed it: it was discoverable only by
 * reading a comment on an unrelated interface. Exported here so the next one
 * cannot.
 *
 * USE THIS for questions of TRUTH — "is this a mount", "should I route it as
 * one". Do NOT use it where the safe direction is to over-include: a gate
 * deciding whether to HIDE something wants the kind alone, so that a demoted row
 * still gets hidden. Truth and safety want different answers here, and the
 * suppression path documents why it deliberately takes the other one.
 */
export function isLiveMountRow(name: string, fileType?: string | null): boolean {
  return (
    (fileType === 'shared' && isSharedPointer(name)) ||
    (fileType === 'chain' && isChainPointer(name))
  )
}

/** The row fields the pointer classifiers read. */
export interface PointerKindRow {
  name?: string | null
  fileType?: string | null
}

/**
 * (I110) Is this row a LIVE `.chain` pointer — `file_type = 'chain'` AND a name
 * ending in `.chain`? THE classifier every surface uses (the drive listing,
 * the share view, the icon). Browser-safe; `@papan/database`'s `isChainPointer` is this
 * same function. The suffix is matched by {@link isChainPointer} — any case (#166).
 *
 * Since I125 a row renamed off its suffix is stored as a plain file (kind `file`,
 * no target), so kind and suffix agree on every stored row; checking both stays the
 * one classifier.
 */
export function isChainPointerRow(row: PointerKindRow | null | undefined): boolean {
  return !!row && row.fileType === 'chain' && typeof row.name === 'string' && isChainPointer(row.name)
}

/** (I110) `.shared` twin of {@link isChainPointerRow} — kind AND suffix. */
export function isSharedPointerRow(row: PointerKindRow | null | undefined): boolean {
  return !!row && row.fileType === 'shared' && typeof row.name === 'string' && isSharedPointer(row.name)
}

/** (I110) A LIVE `.chain` or `.shared` pointer row — {@link isChainPointerRow} ∨ {@link isSharedPointerRow}. */
export function isPointerRow(row: PointerKindRow | null | undefined): boolean {
  return isChainPointerRow(row) || isSharedPointerRow(row)
}

/**
 * (I110) Does the row carry a pointer KIND (`chain` / `shared`)? Since I125 (owner ruling
 * 2026-10-03) the kind never outlives the name: the files chokepoint demotes a pointer renamed
 * off its suffix to a plain file (migration 0251 did the same for older rows), so for a STORED
 * row this answers the same as {@link isPointerRow}. Use it only where the row at hand carries
 * no name — a joined `target_type`, a row shape the mount kernel walks by kind — and say so.
 * Everywhere else, the classifier is {@link isPointerRow}.
 */
export function hasPointerKind(row: { fileType?: string | null } | null | undefined): boolean {
  return pointerKindOf(row) !== null
}

/**
 * Is this row a POINTER ROW that a generic serving surface (the static serve routes `/s/` and pd.app, a
 * download of a file met inside a mount) must refuse — a live `.shared` / `.chain` KIND, or a row that still
 * carries a TARGET column (a #157 waiting Conflict copy keeps its targets)? Its metadata names the target tenant
 * and file. NOT by name alone (E7 review round 4): a plain file the pointer reconcile rejected (`supply.chain`
 * that is not pointer metadata) has its own content and no targets, and every surface serves it. A DEMOTED
 * pointer (I125: kind `file`, targets cleared) is a plain row. Storage's hash-less metadata decision asks
 * `carriesPointerIdentity` instead (its name rule) — a different question.
 */
export function servesAsPointerRow(row: {
  fileType?: string | null; targetTenantId?: string | null; targetFileId?: string | null
} | null | undefined): boolean {
  if (!row) return false
  return hasPointerKind(row) || row.targetTenantId != null || row.targetFileId != null
}

/**
 * E7 (committed reads, review round 3) — does a row carry POINTER IDENTITY that a generic reader (a download, a
 * preview, the share-view / app static routes) must never hand out as file bytes? Its `metadata_json` names the
 * target tenant and file (CLAUDE.md: a View link gets CONTENTS only, never the pointer file). True for:
 *  - a LIVE pointer (`chain` / `shared` kind);
 *  - a #157 waiting CONFLICT copy — stored as a plain `file` that KEEPS its target columns and metadata, named
 *    with its pointer ending, so it can be promoted when the slot frees (`decideShareFileType`,
 *    `insertPointerCopy`, a collab `.shared` edit that lost the slot);
 *  - any row still carrying a target column or a pointer ending.
 * False for an I125-DEMOTED pointer (renamed off its ending: kind `file`, target columns cleared —
 * `pointerDemotionColumns` / migration 0251), whose `metadata_json` IS its bytes. ONE predicate for the storage
 * readers, the static serve routes and the Bin restore check.
 */
export function carriesPointerIdentity(row: {
  name?: string | null; fileType?: string | null; targetTenantId?: string | null; targetFileId?: string | null
} | null | undefined): boolean {
  if (!row) return false
  if (pointerKindOf(row) !== null) return true
  if (row.targetTenantId != null || row.targetFileId != null) return true
  const name = row.name ?? ''
  return isSharedPointer(name) || isChainPointer(name)
}

/**
 * (I110) The pointer KIND a row carries (`'chain'` / `'shared'`), else null. Same caveat as
 * {@link hasPointerKind}: under I125 a stored row's kind always matches its name; read it where
 * only the kind is at hand or the code mirrors the mount kernel's walk.
 */
export function pointerKindOf(row: { fileType?: string | null } | null | undefined): 'chain' | 'shared' | null {
  const k = row?.fileType
  return k === 'chain' || k === 'shared' ? k : null
}

/**
 * Is this row's trailing `.shared` / `.chain` RUNTIME PLUMBING rather than part
 * of the name the user chose?
 *
 * Asked by anything that de-emphasises the suffix -- the drive greys it, on the
 * grounds that `report.pdf.shared` is a mount of `report.pdf` and the wrapper is
 * machinery. That claim is only true of an actual mount, so it needs the row
 * KIND and not just the name.
 *
 * The case that made it wrong is now an ordinary one: downloading a folder with
 * `expandPointers` produces plain folders that KEEP the pointer's name, so a
 * re-upload creates real rows called `ddd.shared`. Greying their suffix says
 * "this is a mount" about something that is not one and cannot become one --
 * `isLiveMountRow` needs `file_type` too, and an uploaded directory has
 * `is_folder: true` with no pointer kind. Measured: such a row comes back
 * `fileType: 'file'` where the real pointer beside it is `'chain'`.
 *
 * `fileType` is authoritative when present; when the row shape does not carry it
 * the extension decides. Same rule as `presentsAsContainer`, and the same
 * reason: a listing that omits the column must keep the old behaviour for real
 * pointers rather than silently un-muting them.
 */
export function pointerSuffixIsMeta(name: string, fileType?: string | null): boolean {
  if (fileType == null) return isSharedPointer(name) || isChainPointer(name)
  return isLiveMountRow(name, fileType)
}

/**
 * (#327) Does this row BEHAVE as a container — something you can link, mount,
 * open or descend into?
 *
 * Use this ANYWHERE a link target, mount target, workspace root or launch target
 * is gated, instead of testing `isFolder` directly. The column and the question
 * are not the same: `isFolder` means "this row is physically a directory", while
 * every such call site means "does this present as a container". A `.shared` /
 * `.chain` POINTER is a metadata stub — `isFolder: false` — that presents as its
 * TARGET, so it answers no to the column and yes to the question.
 *
 * That gap has now caused three separate silent drops: the Track A launch gate
 * (`2b8a052ad`), the workspace link enrichment that made the whole B3 boundary
 * inert, and the AI-access folder check. In each case nothing errored — the row
 * was simply discarded and every layer above reported success. A bare
 * `!row.isFolder` on a path that can receive a pointer id should be read as a
 * bug until proven otherwise.
 *
 * Unlike its `presentsAsMiniapp`/`presentsAsProject` siblings this covers BOTH
 * pointer kinds: those two answer "is the target a package", which `.chain` was
 * deliberately excluded from pending its own routing, whereas "is this a
 * container" is true of any pointer regardless.
 *
 * `fileType` is authoritative when present. When the row shape does not carry it
 * (`FileNode` does not), the extension decides — admission only, never
 * authority: the resolver still re-checks, so a plain folder someone named
 * `x.shared` is admitted here and denied there.
 */
export function presentsAsContainer(name: string, fileType?: string | null, isFolder?: boolean): boolean {
  // BOTH kinds here, unlike the package predicates: "is this a container" is true
  // of any pointer, whereas "is the target a package" excludes `.chain` pending
  // its own routing. And LIVE — a demoted pointer keeps `file_type`, so a
  // kind-only test would keep calling a plain file a container forever.
  if (isLiveMountRow(name, fileType)) return true
  if (fileType == null && (isSharedPointer(name) || isChainPointer(name))) return true
  // `undefined` means the caller doesn't know — only an explicit false rules it
  // out, matching the sibling predicates.
  return isFolder !== false
}

/** Editor enrichment sidecar (`.pmeta` suffix, any case) — for DISPLAY only (greying the
 *  suffix, the icon). Whether a row IS a document's sidecar is {@link isPmetaSidecarName}. */
export function isPmetaFile(name: string): boolean {
  const l = lower(name)
  return l.endsWith(PMETA_SIDECAR_EXT) && l !== PMETA_SIDECAR_EXT
}

/** The editor-enrichment sidecar suffix, as the system writes it (`<doc>.pmeta`). */
export const PMETA_SIDECAR_EXT = '.pmeta'

/**
 * Is this the system-written sidecar NAME of a document (`<doc>.pmeta`) — matched EXACTLY,
 * the one deliberate exception to this module's case-insensitive rule (#166, owner
 * 2026-10-06: "exact match for now")? A sidecar is plumbing the system names and pairs
 * with its document by exact name (`<doc>` + `.pmeta`), not a type a person picks; matching
 * it in any case would make a user's own `notes.PMETA` a second sidecar of `notes` — taking
 * `notes`'s permission instead of its own grants — on some surfaces and not others. Every
 * sidecar decision (permission inheritance, versioning, restore / delete companions, sync
 * skips, protected branches) asks this. A future promote / demote flow like `.shared`'s
 * (a row BECOMES a sidecar only when its document exists) may lift it.
 */
export function isPmetaSidecarName(name: string): boolean {
  return name.endsWith(PMETA_SIDECAR_EXT) && name !== PMETA_SIDECAR_EXT
}

/** Loose HTML file (`.html` / `.htm` suffix, any case). A standalone
 *  (Mode A) mini app is a loose HTML file that loads the SDK. */
export function isHtmlFile(name: string): boolean {
  const l = lower(name)
  return l.endsWith('.html') || l.endsWith('.htm')
}

/** Runnable script file (`.py` / `.sh`, any case) — the extensions the
 *  Script Runner editor registers and the standalone-run worker executes. */
export function isScriptFile(name: string): boolean {
  const l = lower(name)
  return l.endsWith('.py') || l.endsWith('.sh')
}

/**
 * True iff `id` is a synthetic compound mount-file id (`s_<mountId>_<srcId>`).
 *
 * Client-safe pure mirror of `parseMountFileId`/`isMountFileId`
 * (shared/database/mount-id.ts) — duplicated rather than imported because
 * `@papan/database` is server-only. Splits on the first two `_`: prefix must be
 * exactly `s`, mountId a non-empty `_`-free `[A-Za-z0-9]` token, srcId a
 * non-empty remainder. Keep in lockstep with the canonical parser.
 *
 * A compound id is the virtual identity of a file surfaced through a mount
 * (a nested source folder inside a `.shared`/`.chain`). It is NOT a native
 * `files` row, so folder-loading APIs (workspace, icon) can't resolve it —
 * callers that gate a native-only action must exclude it.
 */
export function isMountFileId(id: string | null | undefined): boolean {
  if (!id) return false
  const i1 = id.indexOf('_')
  if (i1 < 0 || id.slice(0, i1) !== 's') return false
  const i2 = id.indexOf('_', i1 + 1)
  if (i2 < 0) return false
  const mountId = id.slice(i1 + 1, i2)
  const sourceFileId = id.slice(i2 + 1)
  return mountId !== '' && sourceFileId !== '' && /^[A-Za-z0-9]+$/.test(mountId)
}

/**
 * Does this drive entry support a debug log? True for the surfaces whose
 * console/stderr output the debug-log feature captures:
 *   - Mode B: a `.miniapp` FOLDER (`isMiniappFolder`),
 *   - Mode A: a loose HTML FILE (`isHtmlFile`), or
 *   - a runnable SCRIPT file (`isScriptFile`) — the standalone Script Runner
 *     tees its run stderr into the file's log.
 * Used to gate the "Debug log" context-menu item and the debug-log routes so
 * the option never appears on arbitrary files (csv, images, pointers, …).
 */
export function supportsDebugLog(name: string, isFolder: boolean): boolean {
  return isFolder ? isMiniappFolder(name) : isHtmlFile(name) || isScriptFile(name)
}

/**
 * A `.chain` row's name MUST end in `.chain`.
 *
 * The suffix is load-bearing, not decoration: `presentsAsFolder` is
 * EXTENSION-gated (`isChainPointer(name) && fileType === 'chain'`), and the
 * listing lift reads the same rule. A caller-supplied name without it therefore
 * writes a row that is a chain in the database and a plain FILE on screen —
 * un-enterable, and classed as a file by the grant helpers too. The default
 * branch has always appended it; a supplied name never did, so
 * `name: 'Sales link'` produced exactly that. Since #518A a human APPROVES this
 * by name first, which makes the mismatch a card promising a mount and a create
 * returning something else.
 *
 * Appending rather than refusing: "Sales link" is a perfectly good intent, and
 * the caller who typed it meant the mount. The AGENT normalizes before building
 * its card so the name on screen is the name that lands; this is the same rule
 * for the HTTP route, which has no card.
 */
export function chainRowName(name: string): string {
  // PRECONDITION: `name` is non-empty. `chainRowName('')` is a bare '.chain' —
  // the very row the special case below exists to prevent — and this function
  // deliberately does not invent a stem for you. Every caller validates the RAW
  // name first (see chain-create.ts), which is what rejects it; a future caller
  // that guards on `!== undefined` instead of truthiness would mint it.
  // A bare `.chain` is NOT already-suffixed, it is a name with no stem — and it
  // lands as a row half this module rejects: `presentsAsFolder`'s bare
  // `endsWith` accepts it (so the drive shows an enterable folder) while
  // `isChainPointer` excludes exactly that string, so `isLiveMountRow` /
  // `presentsAsContainer` say no and the mount is discarded while every layer
  // above reports success. Suffixing it gives it a stem; the caller's own
  // validation is what refuses it outright.
  return isChainPointer(name) ? name : `${name}${CHAIN_POINTER_EXT}`
}


/**
 * Owner D145 (2026-10-21) — RESERVED DRIVE NAMES: names the drive never lets a file or folder take, so the case they
 * would cause never has to be chased. ONE rule, asked by the files create / rename chokepoint
 * (`repos.files.create` / `.update`, `@papan/database`) that every drive write passes — upload (tus), new folder,
 * rename, move-with-a-new-name, copy, Save As, share-view writes, PDApp / agent tools — and by the server's earlier
 * refusals (tus before a byte, unzip per entry). No client calls it: clients translate the server's 400 `RESERVED_NAME`
 * (`driveView.toast.reservedName`). A future reserved name is ONE entry in {@link RESERVED_DRIVE_NAME_RULES}.
 */

/** A SQLite database's working (side) file name: `x.db-wal` / `-shm` / `-journal`, and the `.sqlite` / `.sqlite3`
 *  forms — any case, like every other special ending. The path's LAST segment. */
export const SQLITE_SIDE_FILE_NAME_RE = /\.(db|sqlite|sqlite3)-(wal|shm|journal)$/i

export function looksLikeSqliteSideFileName(nameOrPath: string): boolean {
  return SQLITE_SIDE_FILE_NAME_RE.test(nameOrPath.slice(nameOrPath.lastIndexOf('/') + 1))
}

/** Each rule: the plain-words reason a name is reserved, or null. Add a reserved name here — nowhere else. */
export const RESERVED_DRIVE_NAME_RULES: ReadonlyArray<(name: string) => string | null> = [
  // D145: SQLite keeps a database's working state in these files beside it — a drive item there would be mistaken for
  // one (or stop the database from opening)
  (name) => looksLikeSqliteSideFileName(name) ? `${name} is reserved for a database's working file — choose another name` : null,
]

/** Why `name` can't be a drive item's name (plain words), or null when it can. */
export function reservedDriveNameReason(name: string): string | null {
  for (const rule of RESERVED_DRIVE_NAME_RULES) {
    const why = rule(name)
    if (why) return why
  }
  return null
}

export const RESERVED_NAME_CODE = 'RESERVED_NAME'

/** A drive write refused because its name is reserved ({@link reservedDriveNameReason}) — 400 `RESERVED_NAME`.
 *  Matched by NAME in the API's error middleware (it imports no throwing package). */
export class ReservedDriveNameError extends Error {
  readonly code = RESERVED_NAME_CODE
  constructor(message: string) {
    super(message)
    this.name = 'ReservedDriveNameError'
  }
}

/** Throws {@link ReservedDriveNameError} when `name` is reserved. */
export function assertDriveNameNotReserved(name: string): void {
  const why = reservedDriveNameReason(name)
  if (why) throw new ReservedDriveNameError(why)
}
